import { PageHeader } from '@/components/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  disconnectGoogle,
  fetchGoogleStatus,
  requestAuthorizeUrl,
} from '@/features/google/google-api';
import { ApiError } from '@/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { CheckCircle2, Loader2, Share2, XCircle } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const Route = createFileRoute('/app/settings/google')({
  component: GoogleSettingsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    google: typeof search.google === 'string' ? search.google : undefined,
    reason: typeof search.reason === 'string' ? search.reason : undefined,
  }),
});

function GoogleSettingsPage() {
  const { t } = useTranslation();
  const { google, reason } = Route.useSearch();
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: ['google', 'status'],
    queryFn: fetchGoogleStatus,
    staleTime: 10_000,
  });

  const connect = useMutation({
    mutationFn: () => requestAuthorizeUrl(window.location.pathname),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) => {
      const message =
        err instanceof ApiError ? err.message : t('settings.google.connectErrorGeneric');
      toast.error(message);
    },
  });

  const disconnect = useMutation({
    mutationFn: disconnectGoogle,
    onSuccess: () => {
      toast.success(t('settings.google.disconnectSuccess'));
      queryClient.invalidateQueries({ queryKey: ['google', 'status'] });
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : t('settings.google.disconnectFailed');
      toast.error(message);
    },
  });

  useEffect(() => {
    if (google === 'connected') toast.success(t('settings.google.connectSuccess'));
    if (google === 'error') {
      toast.error(t('settings.google.connectError', { suffix: reason ? `: ${reason}` : '' }));
    }
  }, [google, reason, t]);

  const data = status.data?.status;

  return (
    <div className="max-w-2xl space-y-8">
      <PageHeader
        eyebrow={t('app.layout.section.settings', { defaultValue: 'Settings' })}
        title={t('settings.google.title')}
        description={t('settings.google.description')}
      />

      {google === 'error' ? (
        <Alert variant="destructive">
          <AlertTitle>{t('settings.google.errorTitle')}</AlertTitle>
          <AlertDescription>{reason ?? t('settings.google.errorDefault')}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="border-border/70 shadow-soft">
        <CardHeader>
          <div className="flex items-start gap-3">
            <span className="inline-flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Share2 className="size-4" aria-hidden />
            </span>
            <div className="flex-1 space-y-1">
              <CardTitle className="flex items-center gap-2 text-base">
                {t('settings.google.statusTitle')}
                {data?.connected ? (
                  <Badge variant="success">{t('settings.google.connected')}</Badge>
                ) : !status.isLoading ? (
                  <Badge variant="secondary">{t('settings.google.notConnected')}</Badge>
                ) : null}
              </CardTitle>
              <CardDescription>
                {status.isLoading
                  ? t('settings.google.statusLoading')
                  : data?.connected
                    ? t('settings.google.connected')
                    : t('settings.google.notConnected')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Separator />
          {status.isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              {t('settings.google.loading')}
            </div>
          ) : data?.connected ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-emerald-600" aria-hidden />
                <span className="font-medium">{data.email}</span>
              </div>
              {data.scopes?.length ? (
                <dl className="space-y-1">
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t('settings.google.scopes')}
                  </dt>
                  <dd className="flex flex-wrap gap-1">
                    {data.scopes.map((scope) => (
                      <Badge key={scope} variant="outline" className="font-mono text-[10px]">
                        {scope.replace('https://www.googleapis.com/auth/', '')}
                      </Badge>
                    ))}
                  </dd>
                </dl>
              ) : null}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <XCircle className="size-4" aria-hidden />
              {t('settings.google.notConnectedLabel')}
            </div>
          )}
        </CardContent>
        <CardFooter className="gap-2">
          {data?.connected ? (
            <>
              <Button
                variant="outline"
                disabled={connect.isPending}
                onClick={() => connect.mutate()}
              >
                {connect.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
                {t('settings.google.reconnect')}
              </Button>
              <Button
                variant="destructive"
                disabled={disconnect.isPending}
                onClick={() => disconnect.mutate()}
              >
                {disconnect.isPending ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : null}
                {t('settings.google.disconnect')}
              </Button>
            </>
          ) : (
            <Button disabled={connect.isPending} onClick={() => connect.mutate()}>
              {connect.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              {t('settings.google.connect')}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
